import '~/styles/base.css'

import { Box, Button, ChakraProvider, Flex, Heading, Spinner, Text, VStack } from '@chakra-ui/react'
import { OktaAuth } from '@okta/okta-auth-js'
import { Security } from '@okta/okta-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRootRoute, Link, Outlet } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { Suspense } from 'react'
import { ColorModeProvider } from '~/color-mode'
import { AuthProvider } from '~/contexts/AuthContext'
import { system } from '~/theme'
import { Toaster } from '~/toaster'

const oktaAuth = new OktaAuth({
  issuer: import.meta.env.VITE_OKTA_ISSUER,
  clientId: import.meta.env.VITE_OKTA_CLIENT_ID,
  redirectUri: import.meta.env.VITE_OKTA_REDIRECT_URI || `${window.location.origin}/login/callback`,
  scopes: ['openid', 'profile', 'email'],
})

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
})

function LoadingFallback() {
  return (
    <Flex h="100vh" align="center" justify="center">
      <Box textAlign="center">
        <Spinner size="xl" color="blue.500" />
        <Text mt={4} color="gray.500">
          Loading...
        </Text>
      </Box>
    </Flex>
  )
}

function NotFoundComponent() {
  return (
    <Flex h="100vh" align="center" justify="center">
      <VStack gap={4} textAlign="center">
        <Heading size="2xl">404</Heading>
        <Text fontSize="xl" color="gray.500">
          Page not found
        </Text>
        <Link to="/">
          <Button colorPalette="blue">Go to Dashboard</Button>
        </Link>
      </VStack>
    </Flex>
  )
}

function ErrorComponent({ error }: { error: Error }) {
  // Use plain HTML/CSS since this might render outside ChakraProvider
  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'system-ui, sans-serif',
        backgroundColor: '#f7fafc',
      }}
    >
      <div
        style={{
          textAlign: 'center',
          padding: '32px',
          maxWidth: '600px',
        }}
      >
        <h1 style={{ fontSize: '24px', color: '#e53e3e', marginBottom: '16px' }}>
          Something went wrong
        </h1>
        <p style={{ fontSize: '18px', color: '#718096', marginBottom: '16px' }}>{error.message}</p>
        <div
          style={{
            backgroundColor: '#edf2f7',
            padding: '16px',
            borderRadius: '8px',
            overflow: 'auto',
            maxHeight: '300px',
            textAlign: 'left',
            marginBottom: '16px',
          }}
        >
          <pre
            style={{ fontSize: '12px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', margin: 0 }}
          >
            {error.stack}
          </pre>
        </div>
        <a
          href="/"
          style={{
            display: 'inline-block',
            padding: '8px 16px',
            backgroundColor: '#3182ce',
            color: 'white',
            borderRadius: '6px',
            textDecoration: 'none',
          }}
        >
          Go to Dashboard
        </a>
      </div>
    </div>
  )
}

function RootComponent() {
  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
    // Use window.location for a reliable redirect after auth
    window.location.replace(originalUri || '/_app')
  }

  return (
    <ChakraProvider value={system}>
      <ColorModeProvider defaultTheme="light" enableSystem={false}>
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <Suspense fallback={<LoadingFallback />}>
                <Outlet />
              </Suspense>
              <Toaster />
              {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
            </AuthProvider>
          </QueryClientProvider>
        </Security>
      </ColorModeProvider>
    </ChakraProvider>
  )
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
})
